import { getPortalWebmaster } from '@/api/portal'
import { resolveFileUrl } from '@/components/shared/utils/fileUrl'

export const FALLBACK_SITE_TITLE = '四川中光防雷科技股份有限公司'
export const FALLBACK_FAVICON_URL = '/favicon.ico'

/**
 * 站长公开配置。
 * 依赖后端 GET api/portal/webmaster?locale= 已按语言从 seoLocales 解析出顶层
 * faviconUrl / logoUrl / siteTitle（空则回退默认语言或顶层兜底）。
 */

let webmasterFetchSeq = 0
let inflightWebmasterFetch = null

const emptyWebmaster = () => ({
  faviconUrl: '',
  logoUrl: '',
  siteTitle: '',
  keywords: '',
  description: ''
})

function normalizeWebmasterData(raw) {
  if (!raw) return emptyWebmaster()
  return {
    faviconUrl: String(raw.faviconUrl || '').trim(),
    logoUrl: String(raw.logoUrl || '').trim(),
    siteTitle: String(raw.siteTitle || '').trim(),
    keywords: String(raw.keywords || '').trim(),
    description: String(raw.description || '').trim()
  }
}

export default {
  namespaced: true,
  state: {
    data: emptyWebmaster(),
    loading: false,
    loaded: false,
    locale: ''
  },
  getters: {
    siteTitle(state) {
      return state.data.siteTitle || FALLBACK_SITE_TITLE
    },
    faviconUrl(state) {
      const url = state.data.faviconUrl
      return url ? resolveFileUrl(url) : FALLBACK_FAVICON_URL
    },
    logoUrl(state) {
      const url = state.data.logoUrl
      return url ? resolveFileUrl(url) : ''
    },
    keywords(state) {
      return state.data.keywords || ''
    },
    description(state) {
      return state.data.description || ''
    }
  },
  mutations: {
    SET_WEBMASTER(state, { data, locale }) {
      state.data = normalizeWebmasterData(data)
      state.loaded = true
      state.locale = locale || ''
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    RESET(state) {
      state.data = emptyWebmaster()
      state.loaded = false
      state.locale = ''
      state.loading = false
    }
  },
  actions: {
    async fetchWebmaster({ commit, state, rootState }, options) {
      const { force = false } = options ?? {}
      const locale = rootState.locale.current
      if (!locale) return

      if (!force && state.loaded && state.locale === locale) return

      if (force) {
        webmasterFetchSeq += 1
        inflightWebmasterFetch = null
      }

      if (inflightWebmasterFetch?.locale === locale && !force) {
        return inflightWebmasterFetch.promise
      }

      const seq = ++webmasterFetchSeq
      const task = (async () => {
        commit('SET_LOADING', true)
        try {
          const data = await getPortalWebmaster(locale, { reload: force })
          if (seq !== webmasterFetchSeq) return
          commit('SET_WEBMASTER', { data: data || emptyWebmaster(), locale })
        } catch (error) {
          if (seq !== webmasterFetchSeq) return
          commit('SET_WEBMASTER', { data: emptyWebmaster(), locale })
        } finally {
          if (seq === webmasterFetchSeq) {
            commit('SET_LOADING', false)
          }
        }
      })()

      inflightWebmasterFetch = { locale, promise: task }
      try {
        await task
      } finally {
        if (inflightWebmasterFetch?.promise === task) {
          inflightWebmasterFetch = null
        }
      }
    }
  }
}
